import {
  computed,
  nextTick,
  onBeforeUnmount,
  onMounted,
  ref,
  watch,
  type Ref,
} from 'vue';
import moment from 'moment-timezone';
import numeral from 'numeral';
import { useApiFieldErrors, useColors, useI18n, useIntercom } from '@/composables';
import config from '@/config';
import { $emitter } from '@/services/emitter';
import {
  getBillingCustomerApiRequest,
  postBillingSetupIntentApiRequest,
  postStripePaymentIntentApiRequest,
  postStripePaymentSourceApiRequest,
  postUpdateBillingCardApiRequest,
  postUpdateBillingInfoApiRequest,
} from '@/services/api/billing-api';
import { useStore } from '@/store';
import api, { type AppExeptionsDialogsApi } from '@/components/app-exceptions-resolver/dialogs/api';
import type { AggregatedErrorItem } from '@/components/global/error-aggregator.vue';
import {
  parseFlowErrorsRaw,
  translateFlowErrorsRaw,
} from '@/utils/parseApiErrors';

type PaymentMethods = 'defaultCard' | 'newCard' | 'alipay' | 'wechat' | 'paypal';

type StripeCardRefs = {
  cardNumber: Ref<HTMLElement | null>;
  cardExpiry: Ref<HTMLElement | null>;
  cardCvc: Ref<HTMLElement | null>;
};

export const useUnpaidInvoicesDialog = (cardRefs: StripeCardRefs) => {
  const { t, locale } = useI18n();
  const $colors = useColors();
  const { openLiveSupport } = useIntercom();
  const store = useStore();
  const { errorsDict, applyApiErrors } = useApiFieldErrors();

  const dialogsApi = api as AppExeptionsDialogsApi;

  const last4NumbersOfExistedCard = ref('');
  const paymentMethod = ref<PaymentMethods>('newCard');
  const loadingInvoices = ref(false);
  const loadingStripe = ref(true);
  const showProcessing = ref(false);
  const disabled = ref(false);
  const needToRecheckInvoices = ref(false);
  const wechatCodeReady = ref(false);

  const errors = ref<AggregatedErrorItem[] | string[]>([]);
  const errorsControlActive = ref(false);

  const billingInfo = ref<{ country?: string; billing_address?: Record<string, unknown> }>({
    billing_address: {},
  });

  const unpaidData = ref<Billing.UnpaidData>({
    count: 0,
    invoices: [],
    totalUnpaid: 0,
  });

  const invoicesList = ref<Billing.invoicesList[]>([]);
  const invoicesIds = ref<string[]>([]);

  const paymentIntent = ref<Billing.PaymentIntent>({
    clientSecret: '',
    paymentMethodId: '',
    code: '',
  });

  let intervalCheckUnpaidInvoices: ReturnType<typeof setInterval> | undefined;
  let cardNumberElement: { mount: (el: HTMLElement) => void } | null = null;
  let cardExpiryElement: { mount: (el: HTMLElement) => void } | null = null;
  let cardCvcElement: { mount: (el: HTMLElement) => void } | null = null;
  let stripe: {
    elements: () => {
      create: (type: string, options: Record<string, unknown>) => { mount: (el: HTMLElement) => void };
    };
    createPaymentMethod: (type: string, element: unknown) => Promise<{
      error?: { message: string };
      paymentMethod?: { id: string };
    }>;
    confirmCardSetup: (secret: string) => Promise<{
      error?: { message: string };
      setupIntent?: { id: string };
    }>;
    confirmCardPayment: (secret: string, options: Record<string, unknown>) => Promise<{
      error?: { message: string };
    }>;
    confirmAlipayPayment: (
      secret: string,
      options: Record<string, unknown>,
    ) => Promise<{ error?: { message: string } }>;
  } | null = null;

  const internalPaymentGatewayEnabled = computed(
    () => store.user.internalPaymentGatewayEnabled,
  );

  const paymentDeadline = computed(() =>
    moment.unix(store.billing.subscriptionStart).add(5, 'days').format('ll'),
  );

  const sumFormat = (value: number, format = '0,0.00') =>
    numeral(value / 100).format(format);

  const totalUnpaid = computed(() => sumFormat(unpaidData.value.totalUnpaid));

  const totalUnpaidFormatted = computed(() =>
    sumFormat(unpaidData.value.totalUnpaid, '0.00'),
  );

  const requires3DS = computed(() => {
    const { clientSecret, paymentMethodId, code } = paymentIntent.value;
    return Boolean(clientSecret && paymentMethodId && code === 'authentication_required');
  });

  const payButtonText = computed(() => {
    switch (paymentMethod.value) {
      case 'defaultCard':
        return requires3DS.value
          ? t('Confirm 3DS and pay ${0} With **** {1}', [
              totalUnpaidFormatted.value,
              last4NumbersOfExistedCard.value,
            ])
          : t('Pay ${0} With **** {1}', [
              totalUnpaidFormatted.value,
              last4NumbersOfExistedCard.value,
            ]);
      case 'newCard':
        return t('Pay ${0} With New Card', [totalUnpaidFormatted.value]);
      case 'wechat':
        return t('Pay ${0} With WeChat', [totalUnpaidFormatted.value]);
      case 'alipay':
        return t('Pay ${0} With AliPay', [totalUnpaidFormatted.value]);
      default:
        return '';
    }
  });

  const contentClass = computed(() => {
    const result = ['app-exceptions-resolver-dialog', 'unpaid-invoices-dialog'];

    if (internalPaymentGatewayEnabled.value) {
      result.push('unpaid-invoices-dialog--internal-enabled');
    }

    if (showProcessing.value) {
      result.push('unpaid-invoices-dialog--show-processing');
    }

    return result.join(' ');
  });

  const hasStatusPending = computed(() =>
    unpaidData.value.invoices.find((invoice) =>
      invoice.charges.find((charge) => charge.status === 'pending'),
    ),
  );

  const applySubmitErrors = (error: unknown) => {
    const responseData = (error as { response?: { data?: { errors?: unknown; message?: string } } })
      ?.response?.data;

    if (responseData?.errors) {
      errors.value = translateFlowErrorsRaw(
        parseFlowErrorsRaw(responseData.errors),
        responseData.message || '',
      );
      errorsControlActive.value = true;
    }
  };

  const selectPaymentMethod = (method: PaymentMethods) => {
    paymentMethod.value = method;
  };

  const checkAndClose = () => {
    dialogsApi.collectUnpaidInvoices().then(() => {
      window.location.reload();
    });
  };

  const checkUnpaidInvoices = async () => {
    unpaidData.value = await dialogsApi.getUnpaidInvoices();

    if (unpaidData.value.invoices.length === 0) {
      checkAndClose();
    }
  };

  const actionBeforeLogout = () => {
    if (intervalCheckUnpaidInvoices) {
      clearInterval(intervalCheckUnpaidInvoices);
    }
  };

  const submitErrorHandler = (error: string) => {
    store.setLoading(false);
    dialogsApi.loading = false;
    errors.value = [t('Validation failed'), error];
    errorsControlActive.value = true;
  };

  const submitSuccess = async (token: string) => {
    await postUpdateBillingCardApiRequest(
      {
        token,
        gateway: 'stripe3ds',
      },
      true,
    );
    const customer = await getBillingCustomerApiRequest();
    store.setBillingCustomer(customer);
    checkAndClose();
  };

  const validateBillingAddress = async () => {
    try {
      await postUpdateBillingInfoApiRequest(billingInfo.value);
      const customer = await getBillingCustomerApiRequest();
      store.setBillingCustomer(customer);
    } catch (error) {
      applyApiErrors(error, { nested: true });
      applySubmitErrors(error);
      throw error;
    }
  };

  const payNow = async () => {
    if (requires3DS.value && stripe) {
      const confirm3DS = await stripe.confirmCardPayment(paymentIntent.value.clientSecret, {
        payment_method: paymentIntent.value.paymentMethodId,
      });
      if (confirm3DS.error) {
        $emitter.emit('toast-error', confirm3DS.error.message);
        return;
      }
    }
    checkAndClose();
  };

  const updateAndPay = async () => {
    if (!stripe || !cardNumberElement) {
      return;
    }

    disabled.value = true;
    store.setLoading(true);

    const createPaymentMethod = await stripe.createPaymentMethod('card', cardNumberElement);

    if (createPaymentMethod.error) {
      disabled.value = false;
      submitErrorHandler(createPaymentMethod.error.message);
      return;
    }

    const paymentMethodId = createPaymentMethod.paymentMethod?.id;
    if (!paymentMethodId) {
      disabled.value = false;
      return;
    }

    const setupIntent = (await postBillingSetupIntentApiRequest({
      paymentMethodId,
    }).finally(() => {
      disabled.value = false;
    })) as { status: string; client_secret?: string; id?: string };

    if (
      setupIntent.status === 'requires_action' ||
      setupIntent.status === 'requires_source_action'
    ) {
      const confirmCardSetup = await stripe.confirmCardSetup(setupIntent.client_secret || '');
      if (confirmCardSetup.error) {
        submitErrorHandler(confirmCardSetup.error.message);
        return;
      }
      if (confirmCardSetup.setupIntent?.id) {
        await submitSuccess(confirmCardSetup.setupIntent.id);
      }
      return;
    }

    if (
      setupIntent.status === 'requires_payment_method' ||
      setupIntent.status === 'requires_source'
    ) {
      submitErrorHandler(t('Your card was denied, please provide a new payment method'));
      return;
    }

    if (setupIntent.status === 'succeeded' && setupIntent.id) {
      await submitSuccess(setupIntent.id);
    }
  };

  const payAliPay = async () => {
    disabled.value = true;
    const aliPayPaymentIntent = (await postStripePaymentIntentApiRequest({
      paymentMethodTypes: ['alipay'],
      amount: totalUnpaid.value,
      currency: 'USD',
      invoiceIds: invoicesIds.value,
    })) as { clientSecret: string };

    const { clientSecret } = aliPayPaymentIntent;

    if (!stripe) {
      return;
    }

    stripe
      .confirmAlipayPayment(clientSecret, {
        return_url: window.location.origin,
      })
      .then((resp) => {
        if (resp.error?.message) {
          $emitter.emit('toast-error', resp.error.message);
        }
      });
  };

  const payWechat = async () => {
    disabled.value = true;
    const source = (await postStripePaymentSourceApiRequest({
      type: 'wechat',
      amount: totalUnpaidFormatted.value,
      currency: 'usd',
      invoiceIds: invoicesIds.value,
    })) as Billing.StripePaymentSourceResponse;

    const QRCodeConstructor = (window as { QRCode?: new (...args: unknown[]) => void }).QRCode;
    if (!QRCodeConstructor) {
      return;
    }

    new QRCodeConstructor('wechat-qrcode', {
      text: source.wechat.qr_code_url,
      width: 128,
      height: 128,
      colorDark: $colors.graphite,
      colorLight: $colors.white,
      correctLevel: (window as { QRCode?: { CorrectLevel: { H: unknown } } }).QRCode?.CorrectLevel
        .H,
    });
    wechatCodeReady.value = true;
    needToRecheckInvoices.value = true;
  };

  const pay = async () => {
    await validateBillingAddress();

    switch (paymentMethod.value) {
      case 'defaultCard':
        return payNow();
      case 'newCard':
        return updateAndPay();
      case 'alipay':
        return payAliPay();
      case 'wechat':
        return payWechat();
      default:
        return;
    }
  };

  const initPayPal = () => {
    const renderButtons = () => {
      (window as { paypal?: { Buttons: (options: Record<string, unknown>) => { render: (selector: string) => void } } })
        .paypal?.Buttons({
        style: {
          height: 32,
        },
        createOrder: (_data: unknown, actions: { order: { create: (payload: unknown) => unknown } }) =>
          actions.order.create({
            purchase_units: invoicesList.value,
          }),
        onClick: async () => {
          disabled.value = true;
          try {
            await validateBillingAddress();
          } catch {
            return false;
          }
        },
        onCancel: () => {
          disabled.value = false;
        },
        onApprove: (data: { orderID: string }) => {
          dialogsApi
            .payPalCapture({
              orderID: data.orderID,
            })
            .then(() => {
              checkAndClose();
            });
        },
      }).render('#paypal-button-container');
    };

    if (!document.querySelector('script[src*="paypal.com/sdk/js"]')) {
      const paypalScriptFile = document.createElement('script');
      paypalScriptFile.src = `https://www.paypal.com/sdk/js?client-id=${config.payPalClientId}&disable-funding=card`;
      document.head.appendChild(paypalScriptFile);
      paypalScriptFile.onload = renderButtons;
      return;
    }

    const paypalContainer = document.querySelector('#paypal-button-container');
    if (paypalContainer && !paypalContainer.childNodes.length) {
      renderButtons();
    }
  };

  const mountStripeCardElements = () => {
    if (!stripe) {
      return;
    }

    const style = {
      base: {
        fontSize: '12px',
        color: $colors.graphite,
        lineHeight: '32px',
        fontFamily: 'Lato, Helvetica, sans-serif, -apple-system',
        letterSpacing: '0.6px',
      },
      invalid: {
        color: $colors.graphite,
        borderColor: $colors.red,
      },
    };

    loadingStripe.value = false;

    nextTick(() => {
      if (!cardRefs.cardNumber.value || !cardRefs.cardExpiry.value || !cardRefs.cardCvc.value) {
        return;
      }

      const cardElements = stripe.elements();
      cardNumberElement = cardElements.create('cardNumber', { style });
      cardNumberElement.mount(cardRefs.cardNumber.value);
      cardExpiryElement = cardElements.create('cardExpiry', { style });
      cardExpiryElement.mount(cardRefs.cardExpiry.value);
      cardCvcElement = cardElements.create('cardCvc', { style });
      cardCvcElement.mount(cardRefs.cardCvc.value);
    });
  };

  const onStripeReady = async () => {
    const customerResponse = await getBillingCustomerApiRequest();
    billingInfo.value = customerResponse.billingInfo;
    last4NumbersOfExistedCard.value = customerResponse.paymentMethod?.card?.last4 || '';
    if (last4NumbersOfExistedCard.value) {
      paymentMethod.value = 'defaultCard';
    }
    store.setBillingCustomer(customerResponse);

    const StripeConstructor = (window as { Stripe?: (token: string, options: { locale: string }) => typeof stripe }).Stripe;
    if (!StripeConstructor) {
      $emitter.emit('toast-error', t('Stripe library isn\'t loaded'));
      return;
    }

    stripe = StripeConstructor(config.stripeToken, {
      locale: String(locale.value),
    });

    loadingInvoices.value = true;
    await checkUnpaidInvoices();
    loadingInvoices.value = false;

    unpaidData.value.invoices.forEach((invoice: Billing.UnpaidDataInvoice) => {
      invoice.failures.forEach((failure: Billing.InvoiceFailure) => {
        const { message, paymentIntent: failurePaymentIntent, gateway } = failure;
        if (message) {
          $emitter.emit('toast-error', message, {
            dontHide: true,
          });
        }
        if (
          gateway === 'stripe' &&
          failurePaymentIntent?.clientSecret &&
          failurePaymentIntent?.paymentMethodId
        ) {
          paymentIntent.value = failurePaymentIntent;
        }
      });
    });

    if (unpaidData.value.count) {
      invoicesIds.value = [];
      invoicesList.value = unpaidData.value.invoices.map((item: Billing.UnpaidDataInvoice) => {
        invoicesIds.value.push(item.id);
        return {
          reference_id: item.id,
          invoice_id: item.id,
          amount: {
            value: (item.amount_due / 100).toFixed(2),
          },
        };
      });

      if (internalPaymentGatewayEnabled.value) {
        initPayPal();
      }

      mountStripeCardElements();
      store.setLoading(false);
    } else {
      await dialogsApi.updateUser();
    }
  };

  const loadStripeLibrary = () => {
    if ((window as { Stripe?: unknown }).Stripe) {
      void onStripeReady();
      return;
    }

    const stripeLibrary = document.createElement('script');
    stripeLibrary.src = 'https://js.stripe.com/v3/';
    document.head.appendChild(stripeLibrary);
    stripeLibrary.onload = () => {
      void onStripeReady();
    };
  };

  watch(
    hasStatusPending,
    (value) => {
      if (value) {
        needToRecheckInvoices.value = true;
        showProcessing.value = true;
      }
    },
    { immediate: true },
  );

  watch(needToRecheckInvoices, (value) => {
    if (value) {
      intervalCheckUnpaidInvoices = setInterval(() => {
        void checkUnpaidInvoices();
      }, 5000);
    }
  });

  onMounted(() => {
    loadStripeLibrary();
  });

  onBeforeUnmount(() => {
    actionBeforeLogout();
  });

  return {
    t,
    countryList: store.countryList,
    dialogsApi,
    last4NumbersOfExistedCard,
    paymentMethod,
    loadingInvoices,
    loadingStripe,
    showProcessing,
    disabled,
    unpaidData,
    billingInfo,
    errors,
    errorsControlActive,
    errorsDict,
    paymentDeadline,
    openLiveSupport,
    contentClass,
    payButtonText,
    totalUnpaid,
    internalPaymentGatewayEnabled,
    selectPaymentMethod,
    sumFormat,
    pay,
    actionBeforeLogout,
  };
};
